import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Plus, Pencil, Trash2, X, Check, GripVertical,
  Film, Newspaper, Lock, Unlock
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../../api/client';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

// ── Sortable Video Row ────────────────────────────────
function VideoRow({ v, onEdit, onDelete, onToggle, permModule }) {
  const { can } = useAuth();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: v.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const thumb = v.preview_image?.startsWith('http')
    ? v.preview_image
    : v.video_url?.length <= 15
    ? `https://img.youtube.com/vi/${v.video_url}/sddefault.jpg`
    : null;

  return (
    <tr ref={setNodeRef} style={style}>
      <td style={{ width: 36 }}>
        {can(permModule,'edit') && (
          <span {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--grey-300)', display: 'flex' }}>
            <GripVertical size={16} />
          </span>
        )}
      </td>
      <td style={{ width: 80 }}>
        {thumb
          ? <img src={thumb} alt="" style={{ width: 72, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--grey-100)' }} onError={e => e.target.style.display='none'} />
          : <div style={{ width: 72, height: 44, borderRadius: 6, background: 'var(--orange-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Film size={18} style={{ color: 'var(--orange-200)' }} />
            </div>
        }
      </td>
      <td>
        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{v.video_name}</div>
        {v.video_description && <div style={{ fontSize: 12, color: 'var(--grey-400)', marginTop: 1 }}>{v.video_description.substring(0, 80)}{v.video_description.length > 80 ? '…' : ''}</div>}
      </td>
      <td>
        {v.video_url?.length <= 15
          ? <a href={`https://youtube.com/watch?v=${v.video_url}`} target="_blank" rel="noreferrer"
              style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--orange-500)' }}>
              {v.video_url}
            </a>
          : <span style={{ fontSize: 11, color: 'var(--grey-400)' }}>CDN Stream</span>
        }
      </td>
      <td>
        {v.isPremium
          ? <span className="badge badge-orange"><Lock size={10} /> Premium</span>
          : <span className="badge badge-green"><Unlock size={10} /> Free</span>}
      </td>
      <td style={{ fontWeight: 600, color: 'var(--orange-600)' }}>
        {v.cost ? `₹${v.cost}` : 'Free'}
      </td>
      <td>
        <label className="toggle">
          <input type="checkbox" checked={!v.disable}
            onChange={() => can(permModule,'edit') && onToggle(v, 'disable', !v.disable)} />
          <span className="toggle-track"><span className="toggle-thumb" /></span>
        </label>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          {can(permModule,'edit')   && <button className="btn-icon" onClick={() => onEdit(v)}><Pencil size={14} /></button>}
          {can(permModule,'delete') && <button className="btn-icon" onClick={() => onDelete(v.id)}><Trash2 size={14} /></button>}
        </div>
      </td>
    </tr>
  );
}

// ── Video Form Modal ──────────────────────────────────
function VideoFormModal({ editing, onClose, onSaved, apiPath }) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    editing
      ? reset({ ...editing, isPremium: !!editing.isPremium, disable: !!editing.disable })
      : reset({ video_name:'', title_hindi:'', title_marathi:'', video_url:'',
                 preview_image:'', landscape_img:'', isPremium: false,
                 video_description:'', description_hindi:'', description_marathi:'',
                 cost: 0, release_date:'', preview_video:'', sort_no: 0, disable: false });
  }, [editing, reset]);

  const onSubmit = async (values) => {
    try {
      if (editing) { await api.put(`${apiPath}/${editing.id}`, values); toast.success('Updated'); }
      else         { await api.post(apiPath, values); toast.success('Created'); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{editing ? 'Edit' : 'Add'} Video</span>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="modal-body">
            {/* Names */}
            <div className="form-group">
              <label className="form-label">Title (English) <span className="req">*</span></label>
              <input className={`form-control ${errors.video_name ? 'error' : ''}`}
                {...register('video_name', { required: 'Required' })} />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Title (Hindi)</label>
                <input className="form-control" {...register('title_hindi')} />
              </div>
              <div className="form-group">
                <label className="form-label">Title (Marathi)</label>
                <input className="form-control" {...register('title_marathi')} />
              </div>
            </div>

            {/* Video URL */}
            <div style={{ borderTop: '1px solid var(--grey-100)', paddingTop: 14, marginBottom: 14 }}>
              <div className="form-group">
                <label className="form-label">Video URL / YouTube ID</label>
                <input className="form-control" placeholder="YouTube ID or CDN URL" {...register('video_url')} />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Preview Image URL</label>
                  <input className="form-control" placeholder="https://…" {...register('preview_image')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Landscape Image URL</label>
                  <input className="form-control" placeholder="https://…" {...register('landscape_img')} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Preview Video URL</label>
                <input className="form-control" placeholder="CDN clip URL" {...register('preview_video')} />
              </div>
            </div>

            {/* Description */}
            <div style={{ borderTop: '1px solid var(--grey-100)', paddingTop: 14, marginBottom: 14 }}>
              <div className="form-group">
                <label className="form-label">Description (English)</label>
                <textarea className="form-control" rows={2} {...register('video_description')} />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Description (Hindi)</label>
                  <textarea className="form-control" rows={2} {...register('description_hindi')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Description (Marathi)</label>
                  <textarea className="form-control" rows={2} {...register('description_marathi')} />
                </div>
              </div>
            </div>

            {/* Settings */}
            <div style={{ borderTop: '1px solid var(--grey-100)', paddingTop: 14 }}>
              <div className="form-grid cols-3">
                <div className="form-group">
                  <label className="form-label">Price (₹)</label>
                  <input type="number" className="form-control" {...register('cost')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Release Date</label>
                  <input className="form-control" placeholder="01/05/2024" {...register('release_date')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <input type="number" className="form-control" {...register('sort_no')} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                <label className="toggle">
                  <input type="checkbox" {...register('isPremium')} />
                  <span className="toggle-track"><span className="toggle-thumb" /></span>
                  <span style={{ fontSize: 13 }}>Premium Content</span>
                </label>
                <label className="toggle">
                  <input type="checkbox" {...register('disable')} />
                  <span className="toggle-track"><span className="toggle-thumb" /></span>
                  <span style={{ fontSize: 13 }}>Disabled</span>
                </label>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary"><Check size={14} /> Save Video</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Videos Page factory ───────────────────────────────
function VideosPage({ apiPath, permModule, title, subtitle, Icon }) {
  const { can } = useAuth();
  const [videos, setVideos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [showDisabled, setShowDisabled] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`${apiPath}?includeDisabled=${showDisabled}`);
      setVideos(data.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [apiPath, showDisabled]);

  useEffect(() => { load(); }, [load]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const reordered = arrayMove(videos, videos.findIndex(v => v.id === active.id), videos.findIndex(v => v.id === over.id));
    setVideos(reordered);
    try {
      await api.patch(`${apiPath}/reorder`, { orderedIds: reordered.map(v => v.id) });
      toast.success('Order saved');
    } catch { toast.error('Failed to save order'); load(); }
  };

  const handleToggle = async (v, field, value) => {
    try {
      await api.patch(`${apiPath}/${v.id}/toggle`, { field, value });
      setVideos(prev => prev.map(x => x.id === v.id ? { ...x, [field]: value ? 1 : 0 } : x));
    } catch { toast.error('Failed to update'); }
  };

  const doDelete = async (id) => {
    try { await api.delete(`${apiPath}/${id}`); toast.success('Deleted'); setConfirm(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); setConfirm(null); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{title}</h1>
          <p className="page-subtitle">{subtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label className="toggle">
            <input type="checkbox" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            <span style={{ fontSize: 13 }}>Show disabled</span>
          </label>
          {can(permModule,'create') && (
            <button className="btn btn-primary" onClick={() => { setEditing(null); setModal(true); }}>
              <Plus size={15} /> Add Video
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title"><Icon size={15} style={{ color: 'var(--orange-400)' }} /> {videos.length} video{videos.length !== 1 ? 's' : ''}</span>
          <span style={{ fontSize: 12, color: 'var(--grey-400)' }}>≡ Drag rows to reorder</span>
        </div>
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader" /></div> : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={videos.map(v => v.id)} strategy={verticalListSortingStrategy}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th style={{ width: 80 }}>Thumb</th>
                      <th>Title</th>
                      <th>Video</th>
                      <th>Access</th>
                      <th>Price</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {videos.map(v => (
                      <VideoRow key={v.id} v={v} permModule={permModule}
                        onEdit={v => { setEditing(v); setModal(true); }}
                        onDelete={id => setConfirm(id)}
                        onToggle={handleToggle}
                      />
                    ))}
                    {!videos.length && (
                      <tr><td colSpan={8}>
                        <div className="empty-state"><Icon size={36} /><h3>No videos yet</h3></div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {modal && (
        <VideoFormModal apiPath={apiPath} editing={editing}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); load(); }}
        />
      )}

      <ConfirmDialog open={!!confirm} title="Delete Video" message="This video will be permanently removed."
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)} />
    </div>
  );
}

// ── Named exports ─────────────────────────────────────
export function CreatorVideosPage() {
  return <VideosPage apiPath="/creator-videos" permModule="creator_videos"
    title="Creator Videos" subtitle="Concert and performance videos — drag to reorder"
    Icon={Film} />;
}

export function BlogVideosPage() {
  return <VideosPage apiPath="/blog-videos" permModule="blog_videos"
    title="Blog Videos" subtitle="Testimonials, demos and awareness videos — drag to reorder"
    Icon={Newspaper} />;
}
